import elements from "./elements.json";

interface RGB {
    r: number;
    g: number;
    b: number;
}

interface Element {
    atomicMass: string;
    atomicNumber: number;
    cpkHexColor: string;
    electronegativity: number;
    groupBlock: string;
    name: string;
    symbol: string;
    valence?: number;
}

const elementCache: Record<string, Element> = {};

export default class ElementHelper {
    static get elementCache(): Record<string, Element> {
        return elementCache;
    }

    static getElementBySymbol(symbol: string): Element {
        if (!ElementHelper.elementCache[symbol]) {
            const element = ElementHelper.findElement(symbol);
            if (element) {
                ElementHelper.elementCache[symbol] = element;
            }
        }

        return ElementHelper.elementCache[symbol];
    }

    static getElementByAtomicNumber(number: number): Element {
        if (!ElementHelper.elementCache[number]) {
            const element = ElementHelper.findElementByNumber(number);
            if (element) {
                ElementHelper.elementCache[number] = element;
            }
        }

        return ElementHelper.elementCache[number];
    }

    static findElement(symbol: string): Element | null {
        return elements.find((elem) => elem.symbol === symbol) || null;
    }

    static findElementByNumber(number: number): Element | null {
        return elements.find((elem) => elem.atomicNumber === number) || null;
    }

    static getElementColorBySymbol(symbol: string): number {
        const elem = ElementHelper.getElementBySymbol(symbol);
        return parseInt(elem?.cpkHexColor || "0", 16);
    }

    static getElementColorBySymbolRGB(symbol: string): RGB | null {
        const elem = ElementHelper.getElementBySymbol(symbol);
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
            "#" + elem.cpkHexColor
        );
        return result
            ? {
                  r: parseInt(result[1], 16),
                  g: parseInt(result[2], 16),
                  b: parseInt(result[3], 16),
              }
            : null;
    }

    static getSymbolColorBySymbol(symbol: string): number {
        if (
            symbol === "C" ||
            symbol === "Br" ||
            symbol === "I" ||
            symbol === "N" ||
            symbol === "Xe" ||
            symbol === "Te" ||
            symbol === "O"
        )
            return 0xffffff;
        return 0x000000;
    }

    static getValenceBySymbol(symbol: string): number {
        const elem = ElementHelper.getElementBySymbol(symbol);
        return elem?.valence || 0;
    }

    static getAtomicNumberBySymbol(symbol: string): number {
        const elem = ElementHelper.getElementBySymbol(symbol);
        return elem?.atomicNumber || 0;
    }

    static getAtomicMassBySymbol(symbol: string): number {
        const elem = ElementHelper.getElementBySymbol(symbol);
        return Number(elem?.atomicMass) || 0;
    }

    static isHalogen(symbol: string): boolean {
        // Group 17 occupies the second column from the right in the periodic table
        // and contains fluorine (F), chlorine (Cl), bromine (Br), iodine (I),
        // astatine (At), and tennessine (Ts)
        return ["F", "Cl", "Br", "I", "At", "Ts"].includes(symbol);
    }
}
